/**
 * Experience Planning System V2 - Comprehensive Experience Types Library
 * 
 * This file contains the expanded experience types with decision card structures
 * for the three-phase planning system (Explore → Discuss → Execute)
 */

// Note: This is a foundational structure. Full implementation will include 30-40+ types.
// Starting with key examples to establish the pattern.

export const EXPERIENCE_TYPES_V2 = {
  // ============================================
  // LIFECYCLE EVENTS
  // ============================================
  
  'bar-mitzvah': {
    id: 'bar-mitzvah',
    name: 'Bar/Bat Mitzvah',
    hebrewName: 'בר/בת מצווה',
    category: 'lifecycle',
    frequencyRule: 'once',
    icon: '📜',
    description: 'Coming of age ceremony marking religious adulthood',
    defaultComponents: [
      'A project leading up to the event',
      'A celebration moment showcasing their work',
      'A party of some kind'
    ],
    defaultDecisionCards: [
      {
        id: 'date-selection',
        title: 'Choose the Date',
        description: 'When will the bar/bat mitzvah take place?',
        type: 'date',
        required: true,
        helpText: 'Typically celebrated on or near the 13th Hebrew birthday',
        conditionalTriggers: []
      },
      {
        id: 'project-type',
        title: 'Bar/Bat Mitzvah Project',
        description: 'What meaningful project will they complete?',
        type: 'options',
        required: true,
        options: [
          { 
            value: 'torah-study', 
            label: 'Torah portion study', 
            triggers: ['torah-portion-selection'] 
          },
          { 
            value: 'community-service', 
            label: 'Community service project', 
            triggers: ['service-details'] 
          },
          { 
            value: 'creative', 
            label: 'Creative/artistic project', 
            triggers: ['creative-details'] 
          },
          { 
            value: 'other', 
            label: 'Other', 
            triggers: ['custom-project'] 
          }
        ],
        conditionalTriggers: []
      },
      {
        id: 'celebration-type',
        title: 'Type of Celebration',
        type: 'options',
        required: true,
        options: [
          { 
            value: 'synagogue', 
            label: 'Synagogue service', 
            triggers: ['synagogue-booking'] 
          },
          { 
            value: 'private', 
            label: 'Private ceremony', 
            triggers: ['venue-private'] 
          },
          { 
            value: 'both', 
            label: 'Both synagogue & party', 
            triggers: ['synagogue-booking', 'venue-party'] 
          }
        ],
        conditionalTriggers: []
      },
      {
        id: 'venue-party',
        title: 'Party Venue',
        type: 'gallery',
        conditionalOn: { decision: 'celebration-type', values: ['both', 'private'] },
        helpText: 'Browse venues and save favorites',
        triggers: ['catering-decision', 'guest-count'],
        conditionalTriggers: []
      },
      {
        id: 'catering-decision',
        title: 'Food & Catering',
        type: 'options',
        conditionalOn: { decision: 'celebration-type', values: ['both', 'private'] },
        options: [
          { 
            value: 'full-catering', 
            label: 'Full catering service', 
            triggers: ['catering-vendor'] 
          },
          { 
            value: 'diy', 
            label: 'DIY / family cooking', 
            triggers: ['menu-planning'] 
          },
          { 
            value: 'potluck', 
            label: 'Potluck style', 
            triggers: ['potluck-coordination'] 
          }
        ],
        conditionalTriggers: []
      },
      {
        id: 'guest-list',
        title: 'Guest List',
        type: 'guestlist',
        helpText: 'Who will you invite?',
        conditionalTriggers: []
      },
      {
        id: 'invitations',
        title: 'Invitations',
        type: 'gallery',
        helpText: 'Browse invitation designs',
        conditionalTriggers: []
      }
    ]
  },

  // ============================================
  // ANNUAL - TIER 1 HOLIDAYS
  // ============================================
  
  'passover': {
    id: 'passover',
    name: 'Passover',
    hebrewName: 'פסח',
    category: 'annual',
    tier: 1,
    frequencyRule: 'yearly',
    icon: '🍷',
    description: 'Eight-day festival commemorating the Exodus from Egypt',
    defaultDecisionCards: [
      {
        id: 'seder-nights',
        title: 'Seder Plans',
        type: 'options',
        required: true,
        options: [
          { 
            value: 'both', 
            label: 'Host both nights', 
            triggers: ['seder-first', 'seder-second'] 
          },
          { 
            value: 'first', 
            label: 'First night only', 
            triggers: ['seder-first'] 
          },
          { 
            value: 'second', 
            label: 'Second night only', 
            triggers: ['seder-second'] 
          },
          { 
            value: 'attend', 
            label: 'Attend elsewhere', 
            triggers: [] 
          }
        ],
        conditionalTriggers: []
      },
      {
        id: 'seder-first',
        title: 'First Night Seder',
        type: 'text',
        conditionalOn: { decision: 'seder-nights', values: ['both', 'first'] },
        helpText: 'Where and with whom?',
        triggers: ['guest-list-first', 'menu-first'],
        conditionalTriggers: []
      },
      {
        id: 'cleaning-plan',
        title: 'Cleaning for Passover',
        type: 'options',
        options: [
          { 
            value: 'full-clean', 
            label: 'Full house cleaning', 
            triggers: ['cleaning-schedule'] 
          },
          { 
            value: 'kitchen-only', 
            label: 'Kitchen only', 
            triggers: [] 
          },
          { 
            value: 'skip', 
            label: 'Skip this year', 
            triggers: [] 
          }
        ],
        conditionalTriggers: []
      },
      {
        id: 'passover-groceries',
        title: 'Passover Shopping',
        type: 'text',
        helpText: 'Matzah, wine, seder plate items...',
        triggers: ['grocery-vendor'],
        conditionalTriggers: []
      }
    ]
  },

  // ============================================
  // CYCLICAL - WEEKLY
  // ============================================
  
  'shabbat': {
    id: 'shabbat',
    name: 'Shabbat',
    hebrewName: 'שבת',
    category: 'cyclical',
    frequencyRule: 'weekly',
    icon: '🕯️',
    description: 'Weekly day of rest from Friday evening to Saturday evening',
    defaultDecisionCards: [
      {
        id: 'shabbat-time',
        title: 'Shabbat Dinner Time',
        type: 'text',
        helpText: 'What time will you start?',
        conditionalTriggers: []
      },
      {
        id: 'candle-lighting',
        title: 'Candle Lighting',
        type: 'options',
        options: [
          { value: 'yes', label: 'Yes, we\'ll light candles', triggers: [] },
          { value: 'no', label: 'No candles this time', triggers: [] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'challah',
        title: 'Challah',
        type: 'options',
        options: [
          { 
            value: 'bake', 
            label: 'Bake our own', 
            triggers: ['challah-recipe'] 
          },
          { 
            value: 'buy', 
            label: 'Buy from bakery', 
            triggers: ['challah-vendor'] 
          },
          { 
            value: 'skip', 
            label: 'Skip challah', 
            triggers: [] 
          }
        ],
        conditionalTriggers: []
      },
      {
        id: 'meal-plan',
        title: 'Meal Planning',
        type: 'options',
        options: [
          { 
            value: 'cook', 
            label: 'Cook at home', 
            triggers: ['menu-shabbat'] 
          },
          { 
            value: 'order', 
            label: 'Order food', 
            triggers: ['food-vendor'] 
          },
          { 
            value: 'potluck', 
            label: 'Potluck with friends', 
            triggers: [] 
          }
        ],
        conditionalTriggers: []
      },
      {
        id: 'songs-rituals',
        title: 'Songs & Rituals',
        type: 'options',
        options: [
          { value: 'songs', label: 'Shabbat songs', triggers: [] },
          { value: 'dvar-torah', label: 'D\'var Torah / discussion', triggers: [] },
          { value: 'games', label: 'Family games', triggers: [] },
          { value: 'none', label: 'Just dinner', triggers: [] }
        ],
        conditionalTriggers: []
      }
    ]
  },

  // ============================================
  // CYCLICAL - DAILY
  // ============================================
  
  'morning-routine': {
    id: 'morning-routine',
    name: 'Morning Routine',
    category: 'cyclical',
    frequencyRule: 'daily',
    icon: '☀️',
    description: 'Start your day with intention and meaning',
    defaultDecisionCards: [
      {
        id: 'wake-time',
        title: 'Wake Up Time',
        type: 'text',
        conditionalTriggers: []
      },
      {
        id: 'gratitude',
        title: 'Gratitude Practice',
        type: 'options',
        options: [
          { value: 'modeh-ani', label: 'Modeh Ani prayer', triggers: [] },
          { value: 'gratitude-list', label: 'Gratitude list', triggers: [] },
          { value: 'meditation', label: 'Morning meditation', triggers: [] },
          { value: 'none', label: 'Skip', triggers: [] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'breakfast',
        title: 'Breakfast',
        type: 'options',
        options: [
          { value: 'family', label: 'Family breakfast together', triggers: [] },
          { value: 'quick', label: 'Quick individual meals', triggers: [] },
          { value: 'skip', label: 'Skip breakfast', triggers: [] }
        ],
        conditionalTriggers: []
      }
    ]
  },

  // ============================================
  // MORE LIFECYCLE EVENTS
  // ============================================

  'wedding': {
    id: 'wedding',
    name: 'Wedding',
    hebrewName: 'חתונה',
    category: 'lifecycle',
    frequencyRule: 'once',
    icon: '💍',
    description: 'Celebrate the union of two souls',
    defaultDecisionCards: [
      {
        id: 'date-venue',
        title: 'Date & Venue',
        type: 'date',
        required: true,
        conditionalTriggers: ['guest-list', 'catering']
      },
      {
        id: 'ceremony-style',
        title: 'Ceremony Style',
        type: 'options',
        required: true,
        options: [
          { value: 'orthodox', label: 'Orthodox ceremony', triggers: [] },
          { value: 'conservative', label: 'Conservative ceremony', triggers: [] },
          { value: 'reform', label: 'Reform ceremony', triggers: [] },
          { value: 'interfaith', label: 'Interfaith ceremony', triggers: [] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'chuppah',
        title: 'Chuppah Design',
        type: 'gallery',
        helpText: 'Browse chuppah designs and decorations',
        conditionalTriggers: []
      },
      {
        id: 'ketubah',
        title: 'Ketubah',
        type: 'options',
        options: [
          { value: 'traditional', label: 'Traditional text', triggers: [] },
          { value: 'egalitarian', label: 'Egalitarian text', triggers: [] },
          { value: 'custom', label: 'Custom text', triggers: ['ketubah-text'] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'catering',
        title: 'Catering',
        type: 'options',
        options: [
          { value: 'kosher', label: 'Kosher catering', triggers: ['caterer-selection'] },
          { value: 'kosher-style', label: 'Kosher-style', triggers: ['caterer-selection'] },
          { value: 'non-kosher', label: 'Non-kosher', triggers: ['caterer-selection'] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'guest-list',
        title: 'Guest List',
        type: 'guestlist',
        required: true,
        conditionalTriggers: []
      }
    ]
  },

  'bris': {
    id: 'bris',
    name: 'Bris / Brit Milah',
    hebrewName: 'ברית מילה',
    category: 'lifecycle',
    frequencyRule: 'once',
    icon: '✂️',
    description: 'Covenant ceremony for baby boys',
    defaultDecisionCards: [
      {
        id: 'date-time',
        title: 'Date & Time',
        type: 'date',
        required: true,
        helpText: 'Traditionally on the 8th day after birth',
        conditionalTriggers: []
      },
      {
        id: 'mohel',
        title: 'Mohel Selection',
        type: 'options',
        required: true,
        options: [
          { value: 'orthodox-mohel', label: 'Orthodox mohel', triggers: [] },
          { value: 'modern-mohel', label: 'Modern mohel', triggers: [] },
          { value: 'doctor-mohel', label: 'Doctor/mohel', triggers: [] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'location',
        title: 'Location',
        type: 'options',
        options: [
          { value: 'home', label: 'At home', triggers: [] },
          { value: 'synagogue', label: 'Synagogue', triggers: [] },
          { value: 'hospital', label: 'Hospital', triggers: [] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'celebration',
        title: 'Celebration After',
        type: 'options',
        options: [
          { value: 'small-gathering', label: 'Small family gathering', triggers: ['menu'] },
          { value: 'larger-party', label: 'Larger celebration', triggers: ['menu', 'guest-list'] },
          { value: 'none', label: 'No celebration', triggers: [] }
        ],
        conditionalTriggers: []
      }
    ]
  },

  'baby-naming': {
    id: 'baby-naming',
    name: 'Baby Naming / Simchat Bat',
    hebrewName: 'שמחת בת',
    category: 'lifecycle',
    frequencyRule: 'once',
    icon: '👶',
    description: 'Welcome a new baby girl into the covenant',
    defaultDecisionCards: [
      {
        id: 'date-time',
        title: 'Date & Time',
        type: 'date',
        required: true,
        conditionalTriggers: []
      },
      {
        id: 'hebrew-name',
        title: 'Hebrew Name',
        type: 'text',
        required: true,
        helpText: 'Choose a meaningful Hebrew name',
        conditionalTriggers: []
      },
      {
        id: 'ceremony-style',
        title: 'Ceremony Style',
        type: 'options',
        options: [
          { value: 'synagogue', label: 'At synagogue', triggers: ['rabbi-coordination'] },
          { value: 'home', label: 'At home', triggers: [] },
          { value: 'outdoor', label: 'Outdoor ceremony', triggers: [] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'celebration',
        title: 'Celebration',
        type: 'options',
        options: [
          { value: 'small', label: 'Small family gathering', triggers: ['menu'] },
          { value: 'larger', label: 'Larger celebration', triggers: ['menu', 'guest-list'] }
        ],
        conditionalTriggers: []
      }
    ]
  },

  // ============================================
  // ANNUAL - TIER 1 HOLIDAYS (continued)
  // ============================================

  'rosh-hashanah': {
    id: 'rosh-hashanah',
    name: 'Rosh Hashanah',
    hebrewName: 'ראש השנה',
    category: 'annual',
    tier: 1,
    frequencyRule: 'yearly',
    icon: '🍎',
    description: 'Jewish New Year - Days of Awe begin',
    defaultDecisionCards: [
      {
        id: 'services',
        title: 'Services',
        type: 'options',
        required: true,
        options: [
          { value: 'synagogue', label: 'Attend synagogue', triggers: ['synagogue-selection'] },
          { value: 'home', label: 'Home services', triggers: ['machzor-selection'] },
          { value: 'both', label: 'Both', triggers: ['synagogue-selection', 'machzor-selection'] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'meals',
        title: 'Holiday Meals',
        type: 'options',
        required: true,
        options: [
          { value: 'both-nights', label: 'Host both nights', triggers: ['menu-first', 'menu-second'] },
          { value: 'first-night', label: 'First night only', triggers: ['menu-first'] },
          { value: 'second-night', label: 'Second night only', triggers: ['menu-second'] },
          { value: 'attend', label: 'Attend elsewhere', triggers: [] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'symbolic-foods',
        title: 'Symbolic Foods (Simanim)',
        type: 'gallery',
        helpText: 'Apples & honey, pomegranates, round challah, fish head, etc.',
        conditionalTriggers: []
      },
      {
        id: 'tashlich',
        title: 'Tashlich Ceremony',
        type: 'options',
        options: [
          { value: 'yes', label: 'Perform tashlich', triggers: ['tashlich-location'] },
          { value: 'no', label: 'Skip this year', triggers: [] }
        ],
        conditionalTriggers: []
      }
    ]
  },

  'yom-kippur': {
    id: 'yom-kippur',
    name: 'Yom Kippur',
    hebrewName: 'יום כיפור',
    category: 'annual',
    tier: 1,
    frequencyRule: 'yearly',
    icon: '🕯️',
    description: 'Day of Atonement - holiest day of the year',
    defaultDecisionCards: [
      {
        id: 'services',
        title: 'Services',
        type: 'options',
        required: true,
        options: [
          { value: 'synagogue', label: 'Attend synagogue', triggers: ['synagogue-selection'] },
          { value: 'home', label: 'Home observance', triggers: [] },
          { value: 'both', label: 'Both', triggers: ['synagogue-selection'] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'pre-fast-meal',
        title: 'Pre-Fast Meal',
        type: 'options',
        required: true,
        options: [
          { value: 'host', label: 'Host meal', triggers: ['menu', 'guest-list'] },
          { value: 'attend', label: 'Attend elsewhere', triggers: [] },
          { value: 'family-only', label: 'Family only', triggers: ['menu'] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'break-fast',
        title: 'Break-Fast',
        type: 'options',
        options: [
          { value: 'host', label: 'Host break-fast', triggers: ['menu', 'guest-list'] },
          { value: 'attend', label: 'Attend elsewhere', triggers: [] },
          { value: 'family-only', label: 'Family only', triggers: ['menu'] }
        ],
        conditionalTriggers: []
      }
    ]
  },

  'chanukah': {
    id: 'chanukah',
    name: 'Chanukah',
    hebrewName: 'חנוכה',
    category: 'annual',
    tier: 1,
    frequencyRule: 'yearly',
    icon: '🕎',
    description: 'Festival of Lights - 8 nights of celebration',
    defaultDecisionCards: [
      {
        id: 'menorah',
        title: 'Menorah',
        type: 'options',
        required: true,
        options: [
          { value: 'traditional', label: 'Traditional menorah', triggers: [] },
          { value: 'electric', label: 'Electric menorah', triggers: [] },
          { value: 'both', label: 'Both', triggers: [] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'lighting-location',
        title: 'Where to Light',
        type: 'options',
        options: [
          { value: 'window', label: 'In window (visible)', triggers: [] },
          { value: 'doorway', label: 'By doorway', triggers: [] },
          { value: 'table', label: 'On table', triggers: [] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'gifts',
        title: 'Gift Giving',
        type: 'options',
        options: [
          { value: 'every-night', label: 'Gift each night', triggers: ['gift-planning'] },
          { value: 'one-night', label: 'One special night', triggers: ['gift-planning'] },
          { value: 'gelt-only', label: 'Gelt (money) only', triggers: [] },
          { value: 'none', label: 'No gifts', triggers: [] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'foods',
        title: 'Traditional Foods',
        type: 'gallery',
        helpText: 'Latkes, sufganiyot, etc.',
        conditionalTriggers: []
      },
      {
        id: 'activities',
        title: 'Activities',
        type: 'options',
        options: [
          { value: 'dreidel', label: 'Play dreidel', triggers: [] },
          { value: 'crafts', label: 'Chanukah crafts', triggers: [] },
          { value: 'stories', label: 'Tell the story', triggers: [] },
          { value: 'songs', label: 'Sing Chanukah songs', triggers: [] }
        ],
        conditionalTriggers: []
      }
    ]
  },

  'purim': {
    id: 'purim',
    name: 'Purim',
    hebrewName: 'פורים',
    category: 'annual',
    tier: 1,
    frequencyRule: 'yearly',
    icon: '🎭',
    description: 'Festival of joy and celebration',
    defaultDecisionCards: [
      {
        id: 'megillah',
        title: 'Megillah Reading',
        type: 'options',
        required: true,
        options: [
          { value: 'synagogue', label: 'At synagogue', triggers: [] },
          { value: 'home', label: 'At home', triggers: ['megillah-selection'] },
          { value: 'both', label: 'Both', triggers: ['megillah-selection'] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'costumes',
        title: 'Costumes',
        type: 'gallery',
        helpText: 'Browse costume ideas',
        conditionalTriggers: []
      },
      {
        id: 'hamantaschen',
        title: 'Hamantaschen',
        type: 'options',
        options: [
          { value: 'bake', label: 'Bake at home', triggers: ['recipe-selection'] },
          { value: 'buy', label: 'Buy from bakery', triggers: [] },
          { value: 'both', label: 'Both', triggers: ['recipe-selection'] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'mishloach-manot',
        title: 'Mishloach Manot',
        type: 'options',
        options: [
          { value: 'yes', label: 'Give mishloach manot', triggers: ['recipient-list', 'basket-planning'] },
          { value: 'no', label: 'Skip this year', triggers: [] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'seudah',
        title: 'Purim Seudah',
        type: 'options',
        options: [
          { value: 'host', label: 'Host seudah', triggers: ['menu', 'guest-list'] },
          { value: 'attend', label: 'Attend elsewhere', triggers: [] },
          { value: 'family-only', label: 'Family only', triggers: ['menu'] }
        ],
        conditionalTriggers: []
      }
    ]
  },

  'sukkot': {
    id: 'sukkot',
    name: 'Sukkot',
    hebrewName: 'סוכות',
    category: 'annual',
    tier: 1,
    frequencyRule: 'yearly',
    icon: '🏕️',
    description: 'Festival of Booths - 7 days of dwelling',
    defaultDecisionCards: [
      {
        id: 'sukkah',
        title: 'Sukkah',
        type: 'options',
        required: true,
        options: [
          { value: 'build', label: 'Build our own', triggers: ['sukkah-design', 'materials'] },
          { value: 'rent', label: 'Rent a sukkah', triggers: ['sukkah-vendor'] },
          { value: 'use-existing', label: 'Use existing', triggers: ['sukkah-decorations'] },
          { value: 'none', label: 'No sukkah this year', triggers: [] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'meals',
        title: 'Meals in Sukkah',
        type: 'options',
        options: [
          { value: 'all-meals', label: 'All meals', triggers: ['meal-planning'] },
          { value: 'dinners-only', label: 'Dinners only', triggers: ['meal-planning'] },
          { value: 'special-meals', label: 'Special meals only', triggers: ['meal-planning'] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'decorations',
        title: 'Sukkah Decorations',
        type: 'gallery',
        helpText: 'Browse decoration ideas',
        conditionalTriggers: []
      },
      {
        id: 'lulav-etrog',
        title: 'Lulav & Etrog',
        type: 'options',
        options: [
          { value: 'buy', label: 'Buy set', triggers: ['vendor-selection'] },
          { value: 'borrow', label: 'Borrow from synagogue', triggers: [] },
          { value: 'skip', label: 'Skip this year', triggers: [] }
        ],
        conditionalTriggers: []
      }
    ]
  },

  'shavuot': {
    id: 'shavuot',
    name: 'Shavuot',
    hebrewName: 'שבועות',
    category: 'annual',
    tier: 1,
    frequencyRule: 'yearly',
    icon: '📜',
    description: 'Festival of Weeks - receiving the Torah',
    defaultDecisionCards: [
      {
        id: 'services',
        title: 'Services',
        type: 'options',
        options: [
          { value: 'synagogue', label: 'Attend synagogue', triggers: [] },
          { value: 'home', label: 'Home observance', triggers: [] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'dairy-meals',
        title: 'Dairy Meals',
        type: 'options',
        options: [
          { value: 'host', label: 'Host dairy meal', triggers: ['menu', 'guest-list'] },
          { value: 'attend', label: 'Attend elsewhere', triggers: [] },
          { value: 'family-only', label: 'Family only', triggers: ['menu'] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'cheesecake',
        title: 'Cheesecake',
        type: 'options',
        options: [
          { value: 'bake', label: 'Bake cheesecake', triggers: ['recipe-selection'] },
          { value: 'buy', label: 'Buy cheesecake', triggers: [] },
          { value: 'both', label: 'Both', triggers: ['recipe-selection'] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'all-night-study',
        title: 'All-Night Torah Study',
        type: 'options',
        options: [
          { value: 'yes', label: 'Participate in tikkun leil', triggers: ['location'] },
          { value: 'no', label: 'Skip this year', triggers: [] }
        ],
        conditionalTriggers: []
      }
    ]
  },

  // ============================================
  // ANNUAL - TIER 2 HOLIDAYS
  // ============================================

  'tu-bshevat': {
    id: 'tu-bshevat',
    name: 'Tu B\'Shevat',
    hebrewName: 'ט״ו בשבט',
    category: 'annual',
    tier: 2,
    frequencyRule: 'yearly',
    icon: '🌳',
    description: 'New Year for Trees',
    defaultDecisionCards: [
      {
        id: 'seder',
        title: 'Tu B\'Shevat Seder',
        type: 'options',
        options: [
          { value: 'host', label: 'Host seder', triggers: ['menu', 'guest-list'] },
          { value: 'attend', label: 'Attend elsewhere', triggers: [] },
          { value: 'family-only', label: 'Family only', triggers: ['menu'] },
          { value: 'skip', label: 'Skip this year', triggers: [] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'fruits',
        title: 'Fruits & Nuts',
        type: 'gallery',
        helpText: 'Seven species, dried fruits, etc.',
        conditionalTriggers: []
      },
      {
        id: 'tree-planting',
        title: 'Tree Planting',
        type: 'options',
        options: [
          { value: 'plant', label: 'Plant a tree', triggers: ['location'] },
          { value: 'donate', label: 'Donate to plant trees', triggers: [] },
          { value: 'skip', label: 'Skip this year', triggers: [] }
        ],
        conditionalTriggers: []
      }
    ]
  },

  'lag-bomer': {
    id: 'lag-bomer',
    name: 'Lag B\'Omer',
    hebrewName: 'ל״ג בעומר',
    category: 'annual',
    tier: 2,
    frequencyRule: 'yearly',
    icon: '🔥',
    description: 'Celebration of Rabbi Shimon bar Yochai',
    defaultDecisionCards: [
      {
        id: 'bonfire',
        title: 'Bonfire',
        type: 'options',
        options: [
          { value: 'host', label: 'Host bonfire', triggers: ['location', 'guest-list'] },
          { value: 'attend', label: 'Attend elsewhere', triggers: [] },
          { value: 'skip', label: 'Skip this year', triggers: [] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'activities',
        title: 'Activities',
        type: 'options',
        options: [
          { value: 'archery', label: 'Archery', triggers: [] },
          { value: 'picnic', label: 'Picnic', triggers: ['menu'] },
          { value: 'hike', label: 'Nature hike', triggers: [] }
        ],
        conditionalTriggers: []
      }
    ]
  },

  // ============================================
  // ANNUAL - SOLEMN DAYS
  // ============================================

  'tisha-bav': {
    id: 'tisha-bav',
    name: 'Tisha B\'Av',
    hebrewName: 'תשעה באב',
    category: 'annual',
    tier: 3,
    isSolemnDay: true,
    frequencyRule: 'yearly',
    icon: '🕯️',
    description: 'Day of mourning for the destruction of the Temples',
    defaultDecisionCards: [
      {
        id: 'services',
        title: 'Services',
        type: 'options',
        options: [
          { value: 'synagogue', label: 'Attend synagogue', triggers: [] },
          { value: 'home', label: 'Home observance', triggers: [] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'fasting',
        title: 'Fasting',
        type: 'options',
        options: [
          { value: 'full-fast', label: 'Full fast', triggers: [] },
          { value: 'partial-fast', label: 'Partial fast', triggers: [] },
          { value: 'no-fast', label: 'Not fasting', triggers: [] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'learning',
        title: 'Learning',
        type: 'options',
        options: [
          { value: 'lamentations', label: 'Read Eicha', triggers: [] },
          { value: 'history', label: 'Study history', triggers: [] },
          { value: 'reflection', label: 'Personal reflection', triggers: [] }
        ],
        conditionalTriggers: []
      }
    ]
  },

  'yom-hashoah': {
    id: 'yom-hashoah',
    name: 'Yom HaShoah',
    hebrewName: 'יום השואה',
    category: 'annual',
    tier: 3,
    isSolemnDay: true,
    frequencyRule: 'yearly',
    icon: '🕯️',
    description: 'Holocaust Remembrance Day',
    defaultDecisionCards: [
      {
        id: 'commemoration',
        title: 'Commemoration',
        type: 'options',
        options: [
          { value: 'ceremony', label: 'Attend ceremony', triggers: [] },
          { value: 'museum', label: 'Visit museum', triggers: [] },
          { value: 'home', label: 'Home observance', triggers: [] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'learning',
        title: 'Learning',
        type: 'options',
        options: [
          { value: 'stories', label: 'Read survivor stories', triggers: [] },
          { value: 'documentary', label: 'Watch documentary', triggers: [] },
          { value: 'books', label: 'Read books', triggers: [] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'candle-lighting',
        title: 'Yahrzeit Candle',
        type: 'options',
        options: [
          { value: 'yes', label: 'Light yahrzeit candle', triggers: [] },
          { value: 'no', label: 'Skip this year', triggers: [] }
        ],
        conditionalTriggers: []
      }
    ]
  },

  // ============================================
  // CYCLICAL - WEEKLY
  // ============================================

  'havdalah': {
    id: 'havdalah',
    name: 'Havdalah',
    hebrewName: 'הבדלה',
    category: 'cyclical',
    frequencyRule: 'weekly',
    icon: '🕯️',
    description: 'Ceremony marking the end of Shabbat',
    defaultDecisionCards: [
      {
        id: 'time',
        title: 'Havdalah Time',
        type: 'text',
        helpText: 'Calculate based on sunset',
        conditionalTriggers: []
      },
      {
        id: 'location',
        title: 'Where',
        type: 'options',
        options: [
          { value: 'home', label: 'At home', triggers: [] },
          { value: 'synagogue', label: 'At synagogue', triggers: [] },
          { value: 'community', label: 'Community event', triggers: [] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'rituals',
        title: 'Rituals',
        type: 'options',
        options: [
          { value: 'full', label: 'Full havdalah', triggers: [] },
          { value: 'abbreviated', label: 'Abbreviated', triggers: [] },
          { value: 'blessing-only', label: 'Blessing only', triggers: [] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'activities',
        title: 'After Havdalah',
        type: 'options',
        options: [
          { value: 'melaveh-malkah', label: 'Melaveh Malkah meal', triggers: ['menu'] },
          { value: 'relax', label: 'Relax and unwind', triggers: [] },
          { value: 'activities', label: 'Family activities', triggers: [] }
        ],
        conditionalTriggers: []
      }
    ]
  },

  // ============================================
  // CYCLICAL - MONTHLY
  // ============================================

  'rosh-chodesh': {
    id: 'rosh-chodesh',
    name: 'Rosh Chodesh',
    hebrewName: 'ראש חודש',
    category: 'cyclical',
    frequencyRule: 'monthly',
    icon: '🌙',
    description: 'New month celebration',
    defaultDecisionCards: [
      {
        id: 'observance',
        title: 'How to Observe',
        type: 'options',
        options: [
          { value: 'prayers', label: 'Special prayers', triggers: [] },
          { value: 'meal', label: 'Special meal', triggers: ['menu'] },
          { value: 'learning', label: 'Learn about the month', triggers: [] },
          { value: 'minimal', label: 'Minimal observance', triggers: [] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'women-circle',
        title: 'Women\'s Rosh Chodesh',
        type: 'options',
        options: [
          { value: 'join', label: 'Join women\'s circle', triggers: [] },
          { value: 'host', label: 'Host gathering', triggers: ['guest-list'] },
          { value: 'skip', label: 'Skip this month', triggers: [] }
        ],
        conditionalTriggers: []
      }
    ]
  },

  // ============================================
  // CYCLICAL - DAILY (continued)
  // ============================================

  'bedtime-routine': {
    id: 'bedtime-routine',
    name: 'Bedtime Routine',
    category: 'cyclical',
    frequencyRule: 'daily',
    icon: '🌙',
    description: 'End your day with intention and gratitude',
    defaultDecisionCards: [
      {
        id: 'bedtime',
        title: 'Bedtime',
        type: 'text',
        conditionalTriggers: []
      },
      {
        id: 'rituals',
        title: 'Evening Rituals',
        type: 'options',
        options: [
          { value: 'shema', label: 'Shema prayer', triggers: [] },
          { value: 'gratitude', label: 'Gratitude reflection', triggers: [] },
          { value: 'story', label: 'Bedtime story', triggers: [] },
          { value: 'meditation', label: 'Evening meditation', triggers: [] }
        ],
        conditionalTriggers: []
      },
      {
        id: 'screen-time',
        title: 'Screen Time',
        type: 'options',
        options: [
          { value: 'no-screens', label: 'No screens before bed', triggers: [] },
          { value: 'limited', label: 'Limited screens', triggers: [] },
          { value: 'flexible', label: 'Flexible', triggers: [] }
        ],
        conditionalTriggers: []
      }
    ]
  }
};

// Helper function to get experience type
export const getExperienceTypeV2 = (typeId) => {
  return EXPERIENCE_TYPES_V2[typeId] || null;
};

// Get all experience types by category
export const getExperienceTypesByCategory = (category) => {
  return Object.values(EXPERIENCE_TYPES_V2).filter(type => type.category === category);
};

// Get all experience types
export const getAllExperienceTypesV2 = () => {
  return Object.values(EXPERIENCE_TYPES_V2);
};

// Note: Emoji icons in the data are kept for backward compatibility
// Components should use getExperienceTypeIcon() utility function to get FontAwesome icons
